import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Check, X, ClipboardCheck } from 'lucide-react';
import './AttendanceRegister.css';

/**
 * Attendance register (B-27, ticket #9 — Dominique). After an event has
 * started, the TM / coach of the event's team (or a club admin) marks who
 * actually turned up. The register starts from the RSVP list — Going first —
 * then the people who didn't reply (the chase list: anyone on the team sheet
 * who turned up without replying is already here), then Not going.
 *
 * Saves per person on tap. Tapping the active choice again clears it.
 * Data: GET/PUT /api/clubs/:slug/events/:id/attendance (routes/attendance.js).
 *
 * Rendered only inside an expanded event card, and only when the caller
 * manages this event (canManageThisEvent) AND it has started — the server
 * enforces both again.
 */

const GROUPS = [
  { key: 'going', label: 'Said Going' },
  { key: 'no_reply', label: "Didn't reply" },
  { key: 'not_going', label: 'Said Not going' },
  { key: 'other', label: 'Also marked' },
];

const MAX_PER_SAVE = 200; // mirrors MAX_MARKS_PER_REQUEST server-side

// Optimistic copy of lib/attendance.js applyMarks: same people and order,
// new attendance + counts.
const applyMarksLocally = (old, marks) => {
  if (!old?.people) return old;
  const byId = new Map(marks.map(m => [m.userId, m.status || null]));
  const people = old.people.map(p => (byId.has(p.id) ? { ...p, attendance: byId.get(p.id) } : p));
  return {
    ...old,
    people,
    counts: {
      ...old.counts,
      present: people.filter(p => p.attendance === 'present').length,
      absent: people.filter(p => p.attendance === 'absent').length,
      unmarked: people.filter(p => !p.attendance).length,
    },
  };
};

const parentsLabel = (p) => {
  if (!p.isChild || !Array.isArray(p.parents) || !p.parents.length) return null;
  return p.parents.map(x => x.firstName).filter(Boolean).join(' & ');
};

export const eventHasStarted = (ev) => {
  const t = new Date(ev?.starts_at).getTime();
  return !Number.isNaN(t) && t <= Date.now();
};

const AttendanceRegister = ({ clubSlug, ev }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  const key = ['attendance', clubSlug, ev.id];

  // Loads when the card is expanded (this component only mounts then) so the
  // button can say "14 attended" — the cheap version of the card count.
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => axios.get(`/clubs/${clubSlug}/events/${ev.id}/attendance`).then(r => r.data),
    staleTime: 30 * 1000,
  });

  // The server takes at most 200 marks per request (lib/attendance.js); a
  // club-wide event's "everyone who said Going" can be bigger, so send in
  // batches and keep the last fresh register.
  //
  // Speed (7 Oct, Stuart: "4-5 seconds for the button to go green"): marks
  // show instantly (optimistic) and taps don't wait for each other. When the
  // LAST save in flight settles (success or failure) the register is
  // re-read, so concurrent saves can't overwrite each other's marks and a
  // failed mark doesn't stay green.
  const saveKey = ['attendance-save', clubSlug, ev.id];
  const save = useMutation({
    mutationKey: saveKey,
    onMutate: async (marks) => {
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData(key, (old) => applyMarksLocally(old, marks));
    },
    mutationFn: async (marks) => {
      let fresh = null;
      for (let i = 0; i < marks.length; i += MAX_PER_SAVE) {
        fresh = await axios.put(`/clubs/${clubSlug}/events/${ev.id}/attendance`, { marks: marks.slice(i, i + MAX_PER_SAVE) }).then(r => r.data);
      }
      return fresh;
    },
    onSuccess: () => setError(''),
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: saveKey }) <= 1) queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Could not save — check your connection and try again.');
    },
  });

  const people = data?.people || [];
  const counts = data?.counts || { present: 0, absent: 0 };

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? people.filter(p => p.name.toLowerCase().includes(q)) : people;
  }, [people, filter]);

  const mark = (p, status) => {
    save.mutate([{ userId: p.id, status: p.attendance === status ? null : status }]);
  };

  // WT-29: people whose names this viewer can't see come back as stand-ins
  // (`redacted`, "Junior player"). They keep their group and counts but can't
  // be marked here (the server skips them), so they're never in a save.
  const goingUnmarked = people.filter(p => p.group === 'going' && !p.attendance && !p.redacted);
  const markGoingPresent = () => {
    if (!goingUnmarked.length || save.isPending) return;
    save.mutate(goingUnmarked.map(p => ({ userId: p.id, status: 'present' })));
  };

  return (
    <>
      <div className="att-entry">
        <button
          className="btn btn-secondary btn-sm"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        >
          <ClipboardCheck size={14} /> Mark attendance
        </button>
        {data && (counts.present > 0 || counts.absent > 0) && (
          <span className="att-entry-count">Attended {counts.present}</span>
        )}
      </div>

      {open && createPortal(
        <div className="modal-overlay" onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
          <div className="att-sheet" role="dialog" aria-label="Attendance register" onClick={e => e.stopPropagation()}>
            <div className="att-head">
              <div>
                <h3>Who turned up?</h3>
                <div className="att-sub">{ev.title}</div>
              </div>
              <button className="att-close" aria-label="Close" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>

            <div className="att-summary">
              <span className="att-pill present">{counts.present} present</span>
              <span className="att-pill absent">{counts.absent} absent</span>
              {save.isPending && <span className="att-saving">Saving…</span>}
            </div>

            {goingUnmarked.length > 0 && (
              <button className="btn btn-secondary btn-sm att-bulk" disabled={save.isPending} onClick={markGoingPresent}>
                <Check size={13} /> Mark everyone who said Going as present ({goingUnmarked.length})
              </button>
            )}

            {people.length > 12 && (
              <input
                className="input att-filter"
                placeholder="Find a name…"
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />
            )}

            {error && <div className="att-error">{error}</div>}

            <div className="att-list">
              {isLoading && <div className="att-empty">Loading…</div>}
              {isError && <div className="att-empty">Could not load the register.</div>}
              {data && people.length === 0 && (
                <div className="att-empty">
                  Nobody is on this event's list yet — add players to the team in Club Admin → Teams.
                </div>
              )}
              {data && people.length > 0 && shown.length === 0 && (
                <div className="att-empty">No one matches "{filter}".</div>
              )}
              {GROUPS.map(g => {
                const inGroup = shown.filter(p => p.group === g.key);
                if (!inGroup.length) return null;
                const rows = inGroup.filter(p => !p.redacted);
                const hidden = inGroup.length - rows.length;
                return (
                  <div key={g.key} className="att-group">
                    <div className="att-group-label">{g.label} ({inGroup.length})</div>
                    {rows.map(p => {
                      const guardians = parentsLabel(p);
                      return (
                        <div key={p.id} className={`att-row ${p.attendance ? `is-${p.attendance}` : ''}`}>
                          <div className="att-name">
                            <span>{p.name}</span>
                            {guardians && <span className="att-meta">({guardians})</span>}
                            {p.note && <em className="att-meta"> — {p.note}</em>}
                          </div>
                          <div className="att-actions">
                            <button
                              className={`att-btn present${p.attendance === 'present' ? ' active' : ''}`}
                              aria-pressed={p.attendance === 'present'}
                              onClick={() => mark(p, 'present')}
                            >
                              <Check size={13} /> Present
                            </button>
                            <button
                              className={`att-btn absent${p.attendance === 'absent' ? ' active' : ''}`}
                              aria-pressed={p.attendance === 'absent'}
                              onClick={() => mark(p, 'absent')}
                            >
                              <X size={13} /> Absent
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {hidden > 0 && (
                      <div className="att-hidden">
                        {/* WT-29b (S3): teammates of the viewer's children are named now, so "your teams" includes theirs. */}
                        {rows.length > 0 ? '+ ' : ''}{hidden === 1 ? '1 junior player' : `${hidden} junior players`} not on your or your children's teams — their own team's coach or a club admin marks them.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="att-foot">
              Saved as you tap. Tap a choice again to clear it. It feeds the Attendance report download.
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AttendanceRegister;
